import type { DeliveryMode, Project } from "@szyos/contracts";
import { MAX_DELIVERY_STEPS } from "@szyos/domain";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";
import { api, errorMessage } from "../api";
import { ErrorNote, useToast } from "./feedback";
import { Icon } from "../icons";

const MODES: { value: DeliveryMode; title: string; text: string }[] = [
  {
    value: "merge",
    title: "Mergen",
    text: "Nach deiner Freigabe mergt szyOS den Pull Request. Passt, wenn das Projekt danach von selbst live geht (etwa über CI oder GitHub Pages). Bei szyOS selbst wartet der Auftrag, bis du im Cockpit ausrollst.",
  },
  {
    value: "manual",
    title: "Mergen, dann von Hand ausliefern",
    text: "szyOS mergt, danach hakst du die Schritte unten ab, etwa auf einem Server, den du selbst per SSH betreibst.",
  },
];

/** Auslieferungsweg eines Projekts (Auslieferung, Stufe 1): was nach der Freigabe wirklich passiert. */
export function DeliveryPanel({ project }: { project: Project }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const ids = { steps: useId(), hint: useId() };
  const [mode, setMode] = useState<DeliveryMode>(project.deliveryMode);
  const [steps, setSteps] = useState(project.deliverySteps.join("\n"));
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (input: { mode: DeliveryMode; steps: string[] }) => api.setProjectDelivery(project.id, input),
    onSuccess: (p) => {
      setError(null);
      queryClient.setQueryData(["project", project.id], p);
      toast("Auslieferung gespeichert.");
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const list = steps
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  // Nichts geändert gegenüber dem gespeicherten Stand: der Knopf zeigt „Gespeichert“ statt erneut einzuladen.
  const unchanged =
    mode === project.deliveryMode &&
    (mode === "merge" || list.join("\n") === project.deliverySteps.join("\n"));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (list.length > MAX_DELIVERY_STEPS) return setError(`Höchstens ${MAX_DELIVERY_STEPS} Schritte.`);
    if (list.some((s) => s.length > 300)) return setError("Ein Schritt hat höchstens 300 Zeichen.");
    save.mutate({ mode, steps: list });
  };

  if (!project.repoFullName) {
    return (
      <section className="panel delivery-panel" aria-labelledby="deliveryHead">
        <h2 id="deliveryHead">Auslieferung</h2>
        <p className="muted">
          Ohne Repo gibt es nichts auszuliefern. Ein fertiger Auftrag wird nur als erledigt markiert.
        </p>
      </section>
    );
  }

  return (
    <form className="panel form delivery-panel" onSubmit={submit} noValidate aria-labelledby="deliveryHead">
      <h2 id="deliveryHead">Auslieferung</h2>
      <fieldset className="delivery-modes">
        <legend>Was passiert nach deiner Freigabe?</legend>
        {project.deployApp && (
          <p className="muted">
            Dieses Projekt rollst du per Knopf aus (Bereich „Ausrollen per Knopf“). „Von Hand ausliefern“ geht
            erst, wenn dort das Ausrollziel entfernt ist.
          </p>
        )}
        {MODES.map((m) => (
          <label key={m.value} className="delivery-mode">
            <input
              type="radio"
              name={`delivery-${project.id}`}
              value={m.value}
              checked={mode === m.value}
              disabled={m.value === "manual" && Boolean(project.deployApp)}
              onChange={() => setMode(m.value)}
            />
            <span>
              <strong>{m.title}</strong>
              <span className="muted">{m.text}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {mode === "manual" && (
        <>
          <label htmlFor={ids.steps}>Schritte nach dem Mergen, einer je Zeile</label>
          <textarea
            id={ids.steps}
            rows={5}
            value={steps}
            aria-describedby={ids.hint}
            onChange={(e) => setSteps(e.target.value)}
            placeholder={
              "Repo-Stand per scp auf den Server bringen\nEinrichtungsskript mit --pruefen, dann ohne\nPortprüfung"
            }
          />
          <p className="muted" id={ids.hint}>
            Du hakst sie nach dem Mergen ab. Erst wenn alle abgehakt sind, gilt der Auftrag als ausgeliefert.
          </p>
        </>
      )}
      <ErrorNote error={error} />
      <button
        type="submit"
        className={unchanged ? "btn btn--done" : "btn btn--primary"}
        disabled={save.isPending || unchanged}
      >
        <span className="btn-label">
          <Icon name="check" />{" "}
          {save.isPending ? "Wird gespeichert …" : unchanged ? "Gespeichert" : "Speichern"}
        </span>
      </button>
    </form>
  );
}
